import { useMemo } from 'react';
import {
  Row, Col, Select, Button, Space, Radio, Typography, Tag, Tooltip, Dropdown,
  Divider, Table, Collapse, theme,
} from 'antd';
import { PlusOutlined, ArrowUpOutlined, CloseOutlined } from '@ant-design/icons';

import {
  JOIN_SEPARATOR, FIELD_HINTS, AMOUNT_FIELDS, columnsOf, slotsOf, columnUsage,
  samplesOf, cellValue, groupFields, assignAction, assignColumn,
} from './columnMapping';

const { Text } = Typography;

const PREVIEW_ROWS = 5;
const EXAMPLE_ROWS = 3;

// How an amount reads in the result preview. Only the raw text is available
// here -- parsing belongs to the backend -- so a debit/credit pair is shown as
// one signed value by prefixing, not by arithmetic.
const bareAmount = (v) => String(v ?? '').trim().replace(/^[-+−]\s*/, '');

const amountPreview = (row, mapping, amountShape) => {
  if (amountShape === 'DEBIT_CREDIT') {
    const credit = bareAmount(row[columnsOf(mapping.credit)[0]]);
    if (credit) return { text: `+${credit}`, sign: 1 };
    const debit = bareAmount(row[columnsOf(mapping.debit)[0]]);
    if (debit) return { text: `−${debit}`, sign: -1 };
    return null;
  }
  const signed = String(row[columnsOf(mapping.amount)[0]] ?? '').trim();
  if (!signed) return null;
  return { text: signed, sign: /^[-−]/.test(signed) ? -1 : 1 };
};

/**
 * Step two of the import wizard: say which file column feeds which field.
 *
 * One row per target field, grouped by what the field is for, each showing
 * what the chosen column(s) would actually put there for the first rows. The
 * dropdowns show every column's content next to its name and can be searched
 * by either, because bank headers ("KODA NAMENA", "RAČUN") are often less
 * telling than one look at the data.
 *
 * Free-text fields can join several columns; formats sit in the row of the
 * value they describe; columns nothing reads yet are listed so none is
 * overlooked; and the table at the bottom is the import as it will land.
 */
const ColumnMapper = ({
  fields, headers, sampleRows, mapping, setMapping,
  joinSeparator = JOIN_SEPARATOR,
  amountShape, setAmountShape,
  dateFormat, setDateFormat, dateFormatOptions,
  decimalSep, setDecimalSep,
}) => {
  const { token } = theme.useToken();
  const rows = useMemo(() => sampleRows || [], [sampleRows]);

  const usage = useMemo(() => columnUsage(mapping), [mapping]);
  const labelOf = useMemo(
    () => Object.fromEntries(fields.map(f => [f.field, f.label])),
    [fields],
  );
  const groups = useMemo(() => groupFields(fields), [fields]);

  const columnOptions = useMemo(() => (headers || []).map(h => ({
    value: h,
    label: h,
    samples: samplesOf(h, rows),
  })), [headers, rows]);

  const setSlots = (field, slots) => setMapping((prev) => {
    const next = { ...prev };
    if (!slots.length || (slots.length === 1 && !slots[0])) delete next[field];
    else next[field] = slots.length === 1 ? slots[0] : slots;
    return next;
  });

  const valueOf = (field, row) => {
    const f = fields.find(x => x.field === field);
    return cellValue(field, mapping, row, { joinable: f?.joinable, separator: joinSeparator });
  };

  const renderOption = (field) => (option) => {
    const { value, samples } = option.data;
    const elsewhere = (usage.get(value) || []).filter(f => f !== field);
    return (
      <div style={{ lineHeight: 1.3, padding: '2px 0' }}>
        <Space size={4} wrap>
          <Text strong>{value}</Text>
          {elsewhere.map(f => (
            <Tag key={f} color="blue" bordered={false} style={{ marginInlineEnd: 0 }}>
              → {labelOf[f] || f}
            </Tag>
          ))}
        </Space>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {samples.length ? samples.join(' · ') : '(prazno)'}
          </Text>
        </div>
      </div>
    );
  };

  // Matches the header or anything in the sample, so typing "pica" finds the
  // purpose column without knowing what the bank called it.
  const filterOption = (input, option) => {
    const needle = input.trim().toLowerCase();
    if (!needle) return true;
    return [option.value, ...(option.samples || [])]
      .some(v => String(v).toLowerCase().includes(needle));
  };

  const picker = (f, value, onChange, { error } = {}) => (
    <Select
      style={{ width: '100%', minWidth: 0 }}
      showSearch allowClear
      placeholder="Ni v datoteki"
      value={value || undefined}
      status={error ? 'error' : undefined}
      options={columnOptions}
      filterOption={filterOption}
      optionRender={renderOption(f.field)}
      popupMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 320, maxWidth: 480 }}
      onChange={onChange}
    />
  );

  const renderPickers = (f) => {
    const slots = slotsOf(mapping[f.field]);
    const missing = f.required && !columnsOf(mapping[f.field]).length;

    const change = (index) => (v) => {
      if (v) {
        setSlots(f.field, slots.map((s, i) => (i === index ? v : s)));
      } else {
        // Clearing one part of a join removes that part; clearing the only
        // column unmaps the field.
        setSlots(f.field, slots.length > 1 ? slots.filter((_, i) => i !== index) : []);
      }
    };
    const moveUp = (index) => {
      const next = [...slots];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      setSlots(f.field, next);
    };

    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {slots.map((column, index) => (
          <div
            // Slots have no identity beyond their position.
            key={index}
            style={{ display: 'flex', gap: 4, alignItems: 'center' }}
          >
            {index > 0 && <Text type="secondary" style={{ width: 12, textAlign: 'center' }}>+</Text>}
            <div style={{ flex: 1, minWidth: 0 }}>
              {picker(f, column, change(index), { error: missing })}
            </div>
            {index > 0 && (
              <>
                <Tooltip title="Premakni naprej">
                  <Button
                    size="small" type="text" icon={<ArrowUpOutlined />}
                    aria-label="Premakni naprej" onClick={() => moveUp(index)}
                  />
                </Tooltip>
                <Tooltip title="Odstrani">
                  <Button
                    size="small" type="text" icon={<CloseOutlined />}
                    aria-label="Odstrani" onClick={() => change(index)(undefined)}
                  />
                </Tooltip>
              </>
            )}
          </div>
        ))}
        {f.joinable && slots[slots.length - 1] && (
          <Button
            size="small" type="link" icon={<PlusOutlined />}
            style={{ paddingInline: 0 }}
            onClick={() => setSlots(f.field, [...slots, null])}
          >
            Združi s stolpcem
          </Button>
        )}
      </Space>
    );
  };

  const renderExamples = (f) => {
    if (!columnsOf(mapping[f.field]).length) {
      return <Text type="secondary">—</Text>;
    }
    const examples = rows.slice(0, EXAMPLE_ROWS).map(r => valueOf(f.field, r));
    return examples.map((v, i) => (
      <div
        key={i}
        title={v || undefined}
        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        <Text type={v ? undefined : 'secondary'}>{v || '(prazno)'}</Text>
      </div>
    ));
  };

  // Formats live in the row of the value they describe, not in a toolbar the
  // user has to connect to it.
  const renderExtras = (f) => {
    if (f.field === 'date_to_be_paid') {
      return (
        <Select
          size="small" style={{ minWidth: 200 }}
          value={dateFormat} onChange={setDateFormat}
          options={dateFormatOptions} allowClear
          placeholder="Zapis datuma: samodejno"
        />
      );
    }
    return null;
  };

  const fieldRow = (f) => {
    const missing = f.required && !columnsOf(mapping[f.field]).length;
    const extras = renderExtras(f);
    return (
      <Row
        key={f.field}
        gutter={[16, 4]}
        align="top"
        style={{ padding: '10px 0', borderBottom: `1px solid ${token.colorSplit}` }}
      >
        <Col xs={24} md={6}>
          <Text strong style={missing ? { color: token.colorError } : undefined}>
            {f.label}
            {f.required && <span style={{ color: token.colorError }}> *</span>}
          </Text>
          {FIELD_HINTS[f.field] && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{FIELD_HINTS[f.field]}</Text></div>
          )}
        </Col>
        <Col xs={24} md={10}>
          {renderPickers(f)}
          {extras && <div style={{ marginTop: 6 }}>{extras}</div>}
        </Col>
        <Col xs={24} md={8} style={{ minWidth: 0 }}>
          {renderExamples(f)}
        </Col>
      </Row>
    );
  };

  const amountHeader = (
    <Row
      key="__amount"
      gutter={[16, 4]}
      align="middle"
      style={{ padding: '10px 0', borderBottom: `1px solid ${token.colorSplit}` }}
    >
      <Col xs={24} md={6}>
        <Text strong>Zapis zneska</Text>
      </Col>
      <Col xs={24} md={18}>
        <Space wrap>
          <Radio.Group
            value={amountShape}
            onChange={(e) => setAmountShape(e.target.value)}
            optionType="button" buttonStyle="solid" size="small"
            options={[
              { value: 'SIGNED', label: 'En stolpec (+/-)' },
              { value: 'DEBIT_CREDIT', label: 'Breme / dobro' },
            ]}
          />
          <Select
            size="small" style={{ minWidth: 170 }}
            value={decimalSep} onChange={setDecimalSep}
            options={[
              { value: ',', label: 'Decimalno: , (1.234,56)' },
              { value: '.', label: 'Decimalno: . (1,234.56)' },
            ]}
          />
        </Space>
      </Col>
    </Row>
  );

  // Columns nothing reads yet: the quickest way to notice the one you meant
  // to use, and to put it somewhere with one click.
  const unused = (headers || []).filter(h => !usage.has(h));
  const assignMenu = (column) => ({
    items: fields.map((f) => {
      const action = assignAction(mapping, f.field, f.joinable);
      const current = columnsOf(mapping[f.field]).join(' + ');
      const suffix = {
        set: '',
        append: ` (združi z ${current})`,
        replace: ` (namesto ${current})`,
      }[action];
      return { key: f.field, label: `${f.label}${suffix}` };
    }),
    onClick: ({ key }) => {
      const f = fields.find(x => x.field === key);
      setMapping(prev => assignColumn(prev, key, column, f?.joinable));
    },
  });

  // The import as it will land: mapped fields only, in the order above.
  const previewFields = groups.flatMap(g => g.fields)
    .filter(f => !AMOUNT_FIELDS.includes(f.field) && columnsOf(mapping[f.field]).length);
  const previewColumns = [
    {
      key: '__amount',
      title: 'Znesek',
      render: (_, row) => {
        const a = amountPreview(row, mapping, amountShape);
        if (!a) return <Text type="secondary">—</Text>;
        return (
          <Text style={{ color: a.sign < 0 ? token.colorError : token.colorSuccess, whiteSpace: 'nowrap' }}>
            {a.text}
          </Text>
        );
      },
    },
    ...previewFields.map(f => ({
      key: f.field,
      title: f.label,
      render: (_, row) => {
        const v = valueOf(f.field, row);
        return v ? v : <Text type="secondary">—</Text>;
      },
    })),
  ];
  // Date first, as in every other list of transactions in the app.
  const dateIdx = previewColumns.findIndex(c => c.key === 'date_to_be_paid');
  if (dateIdx > 0) previewColumns.unshift(...previewColumns.splice(dateIdx, 1));

  const rawColumns = (headers || []).map(h => ({
    key: h,
    title: (
      <Space direction="vertical" size={0}>
        <span>{h}</span>
        {(usage.get(h) || []).map(f => (
          <Tag key={f} color="blue" style={{ marginInlineEnd: 0 }}>{labelOf[f] || f}</Tag>
        ))}
      </Space>
    ),
    dataIndex: h,
    render: (v) => String(v ?? ''),
  }));

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Row
          gutter={16}
          style={{ padding: '4px 0', borderBottom: `1px solid ${token.colorSplit}` }}
        >
          <Col xs={0} md={6}><Text type="secondary">Polje</Text></Col>
          <Col xs={0} md={10}><Text type="secondary">Stolpec iz datoteke</Text></Col>
          <Col xs={0} md={8}><Text type="secondary">Primer iz prvih vrstic</Text></Col>
        </Row>
        {groups.map(g => (
          <div key={g.key}>
            <Divider orientation="left" orientationMargin={0} style={{ margin: '16px 0 0' }}>
              {g.title}
            </Divider>
            {g.fields.map((f) => {
              const firstAmount = AMOUNT_FIELDS.includes(f.field)
                && g.fields.find(x => AMOUNT_FIELDS.includes(x.field)) === f;
              return firstAmount ? [amountHeader, fieldRow(f)] : fieldRow(f);
            })}
          </div>
        ))}
      </div>

      {unused.length > 0 && (
        <div>
          <Text type="secondary">
            Neuporabljeni stolpci — kliknite stolpec, da ga dodelite polju:
          </Text>
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {unused.map((h) => {
              const samples = samplesOf(h, rows);
              return (
                <Dropdown key={h} trigger={['click']} menu={assignMenu(h)}>
                  <Tooltip title={samples.length ? samples.join(' · ') : '(prazno)'}>
                    <Tag style={{ cursor: 'pointer', marginInlineEnd: 0 }}>{h}</Tag>
                  </Tooltip>
                </Dropdown>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <Text strong>Predogled uvoza</Text>
        <Text type="secondary"> — prvih {Math.min(PREVIEW_ROWS, rows.length)} vrstic, kot bodo uvožene</Text>
        <Table
          size="small"
          columns={previewColumns}
          dataSource={rows.slice(0, PREVIEW_ROWS).map((r, i) => ({ ...r, __k: i }))}
          rowKey="__k"
          pagination={false}
          scroll={{ x: 'max-content' }}
          style={{ marginTop: 8 }}
        />
      </div>

      <Collapse
        size="small"
        items={[{
          key: 'raw',
          label: 'Izvirne vrstice iz datoteke',
          children: (
            <Table
              size="small"
              columns={rawColumns}
              dataSource={rows.slice(0, PREVIEW_ROWS).map((r, i) => ({ ...r, __k: i }))}
              rowKey="__k"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ),
        }]}
      />
    </Space>
  );
};

export default ColumnMapper;
